import type {
  DocPackCatalog,
  DocPackPage,
  DocPackRect,
  DocPackReadingClass,
} from '@listening/doc-pack';
import { DOC_PACK_READING_CLASS_DEFAULTS } from '@listening/doc-pack';
import {
  type DocPackReadingClassOverrides,
  isDocPackClassEnabled,
} from './reading-plan';

export const docPackReadingLabelColors = {
  body: '#3b82f6',
  heading: '#8b5cf6',
  citation: '#f59e0b',
  'cross-reference': '#84cc16',
  caption: '#06b6d4',
  footnote: '#f97316',
  endnote: '#d946ef',
  equation: '#14b8a6',
  table: '#22c55e',
  'header-footer': '#ef4444',
  'page-number': '#ec4899',
  artifact: '#6b7280',
  'reference-entry': '#a16207',
} as const satisfies Record<DocPackReadingClass, string>;

const docPackReadingLabelMessageKeys = {
  body: 'reading_class_body',
  heading: 'reading_class_heading',
  citation: 'reading_class_citation',
  'cross-reference': 'reading_class_cross_reference',
  caption: 'reading_class_caption',
  footnote: 'reading_class_footnote',
  endnote: 'reading_class_endnote',
  equation: 'reading_class_equation',
  table: 'reading_class_table',
  'header-footer': 'reading_class_header_footer',
  'page-number': 'reading_class_page_number',
  artifact: 'reading_class_artifact',
  'reference-entry': 'reading_class_reference_entry',
} as const satisfies Record<DocPackReadingClass, string>;

type DocPackReadingLabelMessages = Record<
  (typeof docPackReadingLabelMessageKeys)[DocPackReadingClass],
  () => string
>;

/** A label's display name from the calling app's compiled Paraglide messages. */
export const docPackReadingLabelName = (
  messages: DocPackReadingLabelMessages,
  cls: DocPackReadingClass,
) => messages[docPackReadingLabelMessageKeys[cls]]();

const docPackReadingLabelOrder = [
  'body',
  'heading',
  'header-footer',
  'page-number',
  'footnote',
  'endnote',
  'citation',
  'cross-reference',
  'caption',
  'table',
  'equation',
  'reference-entry',
  'artifact',
] as const satisfies readonly DocPackReadingClass[];

export type DocPackLabelRegion = {
  readonly id: string;
  readonly class: DocPackReadingClass;
  readonly rect: DocPackRect;
  readonly read: boolean;
};

export function docPackLabelRegions(
  page: DocPackPage,
  classOverrides: DocPackReadingClassOverrides,
): readonly DocPackLabelRegion[] {
  return page.blocks.flatMap((block) => {
    if (block.bbox === null) return [];
    const segment = page.segments.find(
      (candidate) => candidate.blockId === block.id,
    );
    const read = segment
      ? isDocPackClassEnabled(segment, classOverrides)
      : (classOverrides[block.class] ??
        DOC_PACK_READING_CLASS_DEFAULTS[block.class]);
    return [{ id: block.id, class: block.class, rect: block.bbox, read }];
  });
}

export type DocPackReadingLabel = {
  readonly class: DocPackReadingClass;
  readonly color: string;
  readonly enabled: boolean;
  readonly overridden: boolean;
};

/** The labels present in the document, in display order. A label without an
 * override counts as read when any of its text is read by default. */
export function docPackReadingLabels(
  catalog: Pick<DocPackCatalog, 'pages'>,
  classOverrides: DocPackReadingClassOverrides,
): readonly DocPackReadingLabel[] {
  return docPackReadingLabelOrder.flatMap((cls) => {
    const counts = catalog.pages.flatMap((page) => page.classes[cls] ?? []);
    if (counts.length === 0) return [];
    const override = classOverrides[cls];
    return [
      {
        class: cls,
        color: docPackReadingLabelColors[cls],
        enabled: override ?? counts.some((count) => count.chars > 0),
        overridden: override !== undefined,
      },
    ];
  });
}
